<template>
  <template v-if="versionLoading">
    <span class="loader is-size-2 mx-auto"></span>
  </template>
  <div
    class="card is-clickable"
    v-else-if="version"
  >
    <router-link
      :to="{ name: 'worker-version', params: { versionId: version.id } }"
      target="_blank"
      title="View details for this worker version"
    >
      <div class="card-content p-3 has-text-dark">
        <span
          v-if="version.state"
          class="tag is-capitalized is-pulled-right mt-1"
          :class="stateClass(version.state)"
        >
          {{ version.state }}
        </span>

        <div
          v-if="version.tag"
          class="version-attribute"
        >
          <i
            class="icon-tags"
            title="Tag"
          ></i>
          {{ version.tag }}
        </div>
        <div
          v-if="version.branch"
          class="version-attribute"
        >
          <i
            class="icon-branch"
            title="Branch"
          ></i
          >{{ version.branch }}
        </div>
        <div class="version-attribute">
          created <span :title="version.created">{{ relativeCreated }}</span>
        </div>
      </div>
    </router-link>
    <div
      class="card-footer"
      v-if="processId"
    >
      <!-- Allows to select a worker version in the process configuration view -->
      <a
        class="card-footer-item button is-success"
        :class="{ 'is-loading': addLoading }"
        v-on:click="addWorkerRun"
        title="Add the recommended worker version to the process"
      >
        <i class="icon-plus"></i> Add to process
      </a>
    </div>
  </div>
  <div
    class="notification is-danger"
    v-else-if="versionError"
  >
    Error while loading the recommended worker version:<br />
    {{ versionError }}
  </div>
  <div
    class="notification is-warning"
    v-else
  >
    No recommended worker version
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import { WORKER_VERSION_STATE_COLORS } from '@/config'
import { WorkerVersionState } from '@/enums'
import { ago, errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useNotificationStore, useProcessStore, useWorkerStore } from '@/stores'
import type { UUID } from '@/types'
import type { WorkerVersion } from '@/types/worker'

export default defineComponent({
  emits: {
    'selected-version': (value: WorkerVersion) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  mixins: [truncateMixin],
  props: {
    workerId: {
      type: String as PropType<UUID>,
      required: true,
    },
    processId: {
      type: String as PropType<UUID | null>,
      default: null,
    },
  },
  data: () => ({
    versionLoading: false,
    versionError: null as string | null,
    addLoading: false,
  }),
  computed: {
    ...mapState(useProcessStore, ['processWorkerRuns']),
    ...mapState(useWorkerStore, ['recommendedWorkerVersions']),
    version(): WorkerVersion | null {
      if (this.recommendedWorkerVersions[this.workerId])
        return this.recommendedWorkerVersions[this.workerId]
      return null
    },
    relativeCreated(): string {
      if (!this.version) return ''
      const output = ago(new Date(this.version.created))
      // Add a "on" prefix for absolute dates, so that the date reads `Created on 2042-02-31`
      if (output.endsWith('ago')) return output
      return `on ${output}`
    },
  },
  methods: {
    ...mapActions(useProcessStore, ['createWorkerRun', 'deleteWorkerRun']),
    ...mapActions(useWorkerStore, ['getRecommendedWorkerVersion']),
    ...mapActions(useNotificationStore, ['notify']),
    async fetchRecommendedVersion() {
      this.versionLoading = true
      try {
        await this.getRecommendedWorkerVersion(this.workerId)
      } catch (err) {
        this.versionError = errorParser(err)
      } finally {
        this.versionLoading = false
      }
    },
    async addWorkerRun() {
      if (this.addLoading || !this.processId || !this.version) return
      this.$emit('selected-version', this.version)
      this.addLoading = true
      try {
        await this.createWorkerRun(this.processId, { worker_version_id: this.version.id })
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.addLoading = false
      }
    },
    stateClass(state: WorkerVersionState) {
      return WORKER_VERSION_STATE_COLORS[state]
    },
  },
  watch: {
    workerId: {
      immediate: true,
      handler(newValue: UUID) {
        if (!(newValue in this.recommendedWorkerVersions)) this.fetchRecommendedVersion()
      },
    },
  },
})
</script>

<style lang="scss" scoped>
.card {
  max-width: 20rem;
}

// Merge the border of buttons used in the card footer with the card itself, so that colored borders override the card's
.card-footer-item.button {
  margin: -1px;
  margin-bottom: -var('--bulma-thickness');
}

.version-attribute {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 15ch;
}

.notification {
  padding: 1rem;
}
</style>
